import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, RotateCcw, Bookmark, Lock, Sparkles, Share2, Facebook, GraduationCap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import BreedCard from "@/components/BreedCard";
import MonetizationModule from "@/components/MonetizationModule";
import { API, getGuides } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement";
import { trackGABeginCheckout, trackGAShare, trackGASaveResults } from "@/lib/ga";
import { logError, logWarn } from "@/lib/log";

// Pick the single most breed-relevant training guide for the top match,
// falling back to the foundational learning-theory guide.
function topTrainingGuideSlug(breed) {
  if (!breed) return "how-dogs-learn";
  if (breed.energy >= 4) return "reliable-recall";
  if (breed.hours_alone_ok <= 2) return "separation-anxiety";
  if (breed.barking >= 4 || breed.novice_ok <= 2) return "lead-reactivity";
  if (breed.good_with_pets <= 2) return "resource-guarding";
  return "how-dogs-learn";
}

export default function Results() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [topGuide, setTopGuide] = useState(null);
  const { unlocked } = useEntitlement();
  const nav = useNavigate();

  // Straight-to-Stripe checkout — no intermediate sales page.
  const startCheckout = async () => {
    setUnlocking(true);
    trackGABeginCheckout();
    try {
      const r = await axios.post(`${API}/payments/checkout`,
        { lookup_key: "unlock_lifetime", origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout — please try again");
      setUnlocking(false);
    }
  };

  useEffect(() => {
    const raw = sessionStorage.getItem("pm_results");
    if (!raw) nav("/quiz");
    else {
      const parsed = JSON.parse(raw);
      setData(parsed);
      // Persist top breed IDs so the Adopt page can rank live adoptable dogs
      // against the user's quiz results without an account.
      try {
        const ids = (parsed?.top_matches || []).slice(0, 6).map((m) => m?.breed?.id).filter(Boolean);
        if (ids.length) localStorage.setItem("fetchright.recentMatches", JSON.stringify(ids));
      } catch (error) { logWarn("Could not cache recent matches:", error?.message); }
    }
  }, [nav]);

  useEffect(() => {
    const topBreed = data?.top_matches?.[0]?.breed;
    if (!topBreed) return;
    const slug = topTrainingGuideSlug(topBreed);
    getGuides()
      .then((all) => setTopGuide(all.find((g) => g.slug === slug) || null))
      .catch(() => setTopGuide(null));
  }, [data]);

  const shareResults = async () => {
    const topName = data?.top_matches?.[0]?.breed?.name;
    const shareText = topName
      ? `My top dog breed match on Fetchright is a ${topName}! Take the free 15-question quiz to find yours.`
      : "I found my perfect dog breed match on Fetchright — take the free quiz!";
    const shareUrl = window.location.origin;
    const shareData = { title: "Fetchright — My dog breed match", text: shareText, url: shareUrl };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        trackGAShare("native");
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        trackGAShare("copy");
        toast.success("Link copied — share it anywhere!");
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
          toast.success("Link copied — share it anywhere!");
        } catch (err) {
          logWarn("Share failed:", err?.message);
          toast.error("Couldn't share — please try again");
        }
      }
    }
  };

  const shareToFacebook = () => {
    const shareUrl = window.location.origin;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    trackGAShare("facebook");
    window.open(fbUrl, "_blank", "noopener,noreferrer,width=640,height=640");
  };

  const saveResults = async () => {
    setSaving(true);
    try {
      const answers = JSON.parse(sessionStorage.getItem("pm_answers") || "{}");
      await axios.post(
        `${API}/me/quiz-results`,
        { answers, top_matches: data.top_matches, label: null }
      );
      toast.success("Saved to your library");
      trackGASaveResults();
    } catch (error) {
      logError("Failed to save quiz results:", error);
      toast.error("Couldn't save — please try again");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const top = data.top_matches[0];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-primary font-semibold uppercase tracking-wider text-xs">Your results</p>
          <h1 data-testid="results-heading" className="font-display font-bold text-4xl md:text-5xl text-accent mt-2">
            Meet your top matches
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Ranked by compatibility with the answers you gave. Tap a card for the full profile, cost estimate, and adoptable dogs near you.
          </p>
        </div>
        <Link
          to="/quiz"
          data-testid="results-retake"
          className="pill-btn inline-flex items-center gap-2 rounded-full border-2 border-accent text-accent px-5 py-2.5 text-sm font-semibold hover:bg-accent hover:text-accent-foreground"
        >
          <RotateCcw size={16} /> Retake quiz
        </Link>
        <button
          data-testid="results-save-btn"
          onClick={saveResults}
          disabled={saving}
          className="pill-btn inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          <Bookmark size={16} /> {saving ? "Saving…" : "Save results"}
        </button>
        <button
          data-testid="results-share-btn"
          onClick={shareResults}
          className="pill-btn inline-flex items-center gap-2 rounded-full border-2 border-primary text-primary px-5 py-2.5 text-sm font-semibold hover:bg-primary hover:text-primary-foreground"
        >
          <Share2 size={16} /> Share
        </button>
        <button
          data-testid="results-facebook-btn"
          onClick={shareToFacebook}
          className="pill-btn inline-flex items-center gap-2 rounded-full bg-[#1877F2] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90"
        >
          <Facebook size={16} /> Share to Facebook
        </button>
        {unlocked && data.top_matches.length >= 2 && (
          <Link
            to={`/compare?ids=${data.top_matches.slice(0, 3).map((m) => m.breed.id).join(",")}`}
            data-testid="results-compare-cta"
            className="pill-btn inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90"
          >
            <Sparkles size={16} /> Compare top {Math.min(3, data.top_matches.length)}
          </Link>
        )}
      </div>

      {data.life_stage_note && (
        <div data-testid="life-stage-note" className="rounded-3xl bg-secondary border border-secondary/40 p-6 md:p-7 mb-8 flex items-start gap-4">
          <span className="w-10 h-10 rounded-full bg-accent text-accent-foreground grid place-items-center text-lg shrink-0 font-display font-bold">!</span>
          <div className="text-secondary-foreground">
            <div className="font-display font-bold text-lg text-accent">A note on timing</div>
            <p className="mt-1 text-sm md:text-base leading-relaxed">{data.life_stage_note}</p>
            <a href="/guides/puppy-vs-rescue" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary underline hover:no-underline">
              Read the puppy-vs-rescue comparison →
            </a>
          </div>
        </div>
      )}

      {data.excluded_count > 0 && (
        <div data-testid="excluded-notice" className="rounded-2xl bg-muted border border-border/60 p-5 mb-8 flex items-start gap-3">
          <span className="text-lg" aria-hidden>ⓘ</span>
          <div className="text-sm text-accent/90">
            <div className="font-semibold">
              {data.excluded_count} breed{data.excluded_count === 1 ? "" : "s"} hidden by your local rules
            </div>
            <div className="text-muted-foreground mt-0.5">
              {data.excluded_sample?.slice(0, 3).map((b) => b.name).join(", ")}
              {data.excluded_count > 3 ? ` and ${data.excluded_count - 3} more` : ""} — filtered because of weight or breed restrictions you set.
            </div>
          </div>
        </div>
      )}

      {top && (
        <div className="rounded-[2rem] bg-secondary p-8 md:p-10 mb-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-wider">
              Your #1 match — {top.compatibility}%
            </span>
            <h2 className="font-display font-bold text-4xl text-accent mt-4">
              {top.breed.name}
            </h2>
            <p className="text-secondary-foreground/80 mt-3">{top.breed.summary}</p>
            {top.reasons?.length > 0 && (
              <ul className="mt-5 space-y-1.5 text-sm text-accent">
                {top.reasons.map((r) => (
                  <li key={r} className="flex gap-2"><span className="text-primary">✓</span>{r}</li>
                ))}
              </ul>
            )}
            <Link
              to={`/breeds/${top.breed.id}`}
              data-testid="top-match-cta"
              className="pill-btn mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold"
            >
              See full profile <ArrowRight size={16} />
            </Link>
          </div>
          <div className="rounded-3xl overflow-hidden aspect-[4/3]">
            <img src={top.breed.image} alt={top.breed.name} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      {top && topGuide && (
        <Link
          to={`/guides/${topGuide.slug}`}
          data-testid="results-training-guide"
          className="group flex items-center gap-4 rounded-3xl border border-border/60 bg-card warm-shadow p-5 md:p-6 mb-12 hover:-translate-y-0.5 transition-transform"
        >
          <span className="w-12 h-12 shrink-0 rounded-2xl bg-primary/10 text-primary grid place-items-center">
            <GraduationCap size={22} />
          </span>
          <div className="min-w-0">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">
              Expert training guide for your {top.breed.name}
            </span>
            <h3 className="font-display font-bold text-lg md:text-xl text-accent leading-tight truncate">
              {topGuide.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-1">{topGuide.excerpt}</p>
          </div>
          <ArrowRight size={18} className="ml-auto shrink-0 text-primary group-hover:translate-x-1 transition-transform" />
        </Link>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {unlocked ? (
          data.top_matches.slice(1).map((m) => (
            <BreedCard
              key={m.breed.id}
              breed={m.breed}
              score={m.compatibility}
              reasons={m.reasons}
            />
          ))
        ) : (
          <div
            data-testid="results-paywall"
            className="sm:col-span-2 lg:col-span-3 relative rounded-[2rem] bg-accent text-accent-foreground p-8 md:p-12 overflow-hidden warm-shadow"
          >
            {/* Ghost preview of locked cards */}
            <div className="absolute inset-0 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 p-8 md:p-12 pointer-events-none blur-md opacity-25 select-none">
              {data.top_matches.slice(1).map((m) => (
                <div
                  key={m.breed.id}
                  className="rounded-2xl bg-background/40 h-full min-h-[220px] flex items-end p-4"
                >
                  <span className="font-display font-bold">{m.breed.name}</span>
                </div>
              ))}
            </div>

            <div className="relative text-center max-w-lg mx-auto">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
                <Lock size={12} /> {data.top_matches.length - 1} more matches locked
              </span>
              <h3 className="mt-5 font-display font-bold text-3xl md:text-4xl leading-tight">
                Unlock your other {data.top_matches.length - 1} matches for $2.
              </h3>
              <p className="mt-4 text-accent-foreground/75 text-base">
                One payment. Lifetime access. See every breed we ranked for you, the reasons, and the full cost breakdown.
              </p>
              <button
                onClick={startCheckout}
                disabled={unlocking}
                data-testid="results-unlock-cta"
                className="pill-btn mt-7 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-semibold text-base disabled:opacity-60"
              >
                <Sparkles size={16} /> {unlocking ? "Opening secure checkout…" : "Unlock for $2"}
              </button>
              <p className="mt-3 text-xs text-accent-foreground/60">
                Secure checkout by Stripe · no subscription
              </p>
            </div>
          </div>
        )}
      </div>

      {top && <MonetizationModule breedId={top.breed.id} />}
    </div>
  );
}
